<template>
  <div>
    <div class="top-img" :class="{ 'image-loaded': imageLoaded }">
      <img loading="lazy" :src="experienceImage" class="top-bg" @load="onImageLoad"/>
      <div class="overlay">
        <h1 class="experience-title">{{ experience?.name }}</h1>
      </div>
    </div>

    <div class="experience-content">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="short-description" v-html="sanitizeHtml(experience?.shortDescription)"></div>
          </div>
        </div>

        <!-- Availability Check Section -->
        <div class="availability-section">
          <div class="row">
            <!-- Layout for PerPerson: Two independent boxes side by side -->
            <template v-if="isPerPersonPricing">
              <!-- Availability Card -->
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="availability-card">
                  <div class="availability-header">
                    <h3 class="availability-title">{{ $t("experiences.availability") }}</h3>
                  </div>

                  <AvailabilityForm
                    :people="availabilityForm.people"
                    :language="availabilityForm.language"
                    :date="availabilityForm.date"
                    @update:people="availabilityForm.people = $event"
                    @update:language="availabilityForm.language = $event"
                    @update:date="availabilityForm.date = $event"
                    @submit="checkAvailability"
                  />

                  <SlotsDisplay
                    v-if="availableSlots.length > 0"
                    :availableSlots="availableSlots"
                    :nextAvailableSlots="nextAvailableSlots"
                    :showPrices="!!experience.hasVariablePrices"
                    priceType="PerPerson"
                    @select-slot="selectSlot"
                  />

                  <NoAvailability
                    v-else-if="availabilityChecked"
                    :nextAvailableSlots="nextAvailableSlots"
                    @select-slot="selectSlot"
                  />
                </div>
              </div>

              <!-- Price Box - PerPerson with age brackets -->
              <div class="col-12 col-lg-2 price-box-wrapper">
                <PriceBox
                  priceType="PerPerson"
                  :ageBracketPrices="experience.ageBracketPrices"
                  :openDatesAgeBracketPrices="openDatesAgeBracketPrices"
                  :hasVariablePrices="!!experience.hasVariablePrices"
                  :fromPrice="experience.fromPrice || 0"
                  :selectedSlot="selectedSlot"
                />
              </div>
            </template>

            <!-- Layout for PerExperience -->
            <template v-else>
              <!-- Availability Card -->
              <div class="col-12 col-lg-8 offset-lg-2">
                <div class="availability-card">
                  <div class="availability-header">
                    <h3 class="availability-title">{{ $t("experiences.availability") }}</h3>
                  </div>

                  <AvailabilityForm
                    :people="availabilityForm.people"
                    :language="availabilityForm.language"
                    :date="availabilityForm.date"
                    @update:people="availabilityForm.people = $event"
                    @update:language="availabilityForm.language = $event"
                    @update:date="availabilityForm.date = $event"
                    @submit="checkAvailability"
                  />

                  <SlotsDisplay
                    v-if="availableSlots.length > 0"
                    :availableSlots="availableSlots"
                    :nextAvailableSlots="nextAvailableSlots"
                    :showPrices="!!experience.hasVariablePrices"
                    priceType="PerExperience"
                    @select-slot="selectSlot"
                  />

                  <NoAvailability
                    v-else-if="availabilityChecked"
                    :nextAvailableSlots="nextAvailableSlots"
                    @select-slot="selectSlot"
                  />
                </div>
              </div>

              <!-- Price Box - PerExperience single price -->
              <div v-if="experience?.hasVariablePrices || (experience?.price && experience.price > 0)" class="col-12 col-lg-2 price-box-wrapper">
                <PriceBox
                  priceType="PerExperience"
                  :price="experience.price"
                  :hasVariablePrices="!!experience.hasVariablePrices"
                  :fromPrice="experience.fromPrice || 0"
                  :selectedSlot="selectedSlot"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- Reservation Form -->
        <div v-if="showReservationForm" class="reservation-section">
          <ReservationForm
            :experience="experience"
            :selectedSlot="selectedSlot"
            :numberOfPeople="availabilityForm.people"
            @reservation-submitted="handleReservationSubmitted"
          />
        </div>

        <!-- Image Gallery Carousel -->
        <div v-if="experience?.images && experience.images.length > 0" class="image-gallery-section">
          <div class="row justify-content-center">
            <div class="col-12 col-lg-8">
              <div class="image-gallery-card">
                <div class="image-carousel">
                  <button class="carousel-arrow left" @click="prevImage" :disabled="currentImageIndex === 0" aria-label="Anterior">&#10094;</button>
                  <img
                    :src="joinPaths(experience.images[currentImageIndex])"
                    :alt="experience.name"
                    class="gallery-main-image"
                    @click="openGallery(currentImageIndex)"
                  />
                  <button class="carousel-arrow right" @click="nextImage" :disabled="currentImageIndex >= experience.images.length - 1" aria-label="Seguinte">&#10095;</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <FullScreenFile
          v-if="isGalleryOpen"
          :gallery="galleryImages"
          :startIndex="lightboxStartIndex"
          @closeRequest="closeGallery"
        />

        <!-- Description -->
        <div class="description-section">
          <h3>{{ $t("experiences.description") }}</h3>
          <div v-if="experience && experience.description" v-html="sanitizeHtml(experience.description)"></div>
          <div v-else class="no-description">{{ $t('experiences.noDescription') }}</div>
        </div>
      </div>
    </div>

    <!-- Lead Capture Modal -->
    <div v-if="showLeadCapture" class="modal-overlay" @click="showLeadCapture = false">
      <div class="modal-content" @click.stop>
        <h3>{{ $t("experiences.requestAvailability") }}</h3>
        <form @submit.prevent="submitLeadCapture">
          <div class="form-group">
            <label>{{ $t("name") }}</label>
            <input type="text" v-model="leadForm.name" class="form-control" required>
          </div>
          <div class="form-group">
            <label>{{ $t("email") }}</label>
            <input type="email" v-model="leadForm.email" class="form-control" required>
          </div>
          <div class="form-group">
            <label>{{ $t("phoneNumber") }}</label>
            <input type="tel" v-model="leadForm.phone" class="form-control" required>
          </div>
          <div class="form-group">
            <label>{{ $t("experiences.message") }}</label>
            <textarea v-model="leadForm.message" class="form-control" rows="3"></textarea>
          </div>
          <div class="form-actions">
            <button type="button" @click="showLeadCapture = false" class="btn btn-secondary">
              {{ $t("cancel") }}
            </button>
            <button type="submit" class="btn btn-primary">
              {{ $t("experiences.sendRequest") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { API_HELPER } from "@/helpers/api.js";
import ReservationForm from "@/components/ReservationForm.vue";
import FullScreenFile from "@/components/FullScreenFile.vue";
import { sanitizeHtml } from '@/helpers/sanitize.js';

// Extracted components
import AvailabilityForm from "@/components/experience/AvailabilityForm.vue";
import SlotsDisplay from "@/components/experience/SlotsDisplay.vue";
import NoAvailability from "@/components/experience/NoAvailability.vue";
import PriceBox from "@/components/experience/PriceBox.vue";

// Services
import { experienceAvailabilitiesService } from "@/services/experienceAvailabilities.service.js";
import { experiencesService } from "@/services/experiences.service.js";
import {
  parseDateString,
  isSlotTimePast,
  formatDateForApi,
  getUnavailabilityReason
} from "@/services/slotAvailability.service.js";

export default {
  components: {
    ReservationForm,
    FullScreenFile,
    AvailabilityForm,
    SlotsDisplay,
    NoAvailability,
    PriceBox
  },
  data() {
    return {
      imageLoaded: false,
      availabilityForm: {
        people: 1,
        language: 'pt',
        date: ''
      },
      availableSlots: [],
      nextAvailableSlots: [],
      availabilityChecked: false,
      showLeadCapture: false,
      showReservationForm: false,
      selectedSlot: null,
      // Age-bracket prices shared by every bookable date, when they all match (e.g. only next year's dates are open)
      openDatesAgeBracketPrices: null,
      leadForm: {
        name: '',
        email: '',
        phone: '',
        message: ''
      },
      currentImageIndex: 0,
      isGalleryOpen: false,
      lightboxStartIndex: 0
    };
  },
  computed: {
    ...mapGetters('experiences', ['experience']),
    experienceImage() {
      if (this.experience && this.experience.images && this.experience.images.length > 0) {
        return this.joinPaths(this.experience.images[0]);
      }
      return '/default-image.jpg';
    },
    galleryImages() {
      if (!this.experience?.images) {
        return [];
      }
      return this.experience.images.map(image => ({ image }));
    },
    isPerPersonPricing() {
      return this.experience?.priceType === 'PerPerson' &&
             this.experience?.ageBracketPrices &&
             Object.keys(this.experience.ageBracketPrices).length > 0 &&
             Object.values(this.experience.ageBracketPrices).some(p => p > 0);
    }
  },
  async mounted() {
    const slug = this.$route.params.experienceSlug;
    if (slug) {
      await this.getBySlug(slug);
      this.loadOpenDatesPrices();
    }
    this.updateHtmlLang();
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('no-scroll');
    }
  },
  watch: {
    '$route.params.experienceSlug': {
      immediate: false,
      async handler(newSlug) {
        if (newSlug) {
          await this.getBySlug(newSlug);
          this.loadOpenDatesPrices();
        }
      }
    },
    experience() {
      this.currentImageIndex = 0;
    },
    '$i18n.locale': {
      handler() {
        this.updateHtmlLang();
      }
    }
  },
  methods: {
    ...mapActions({
      getBySlug: 'experiences/getBySlug'
    }),
    sanitizeHtml,
    joinPaths(relativePath) {
      return API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, relativePath]);
    },
    onImageLoad() {
      this.imageLoaded = true;
    },
    async checkAvailability() {
      if (!this.experience || !this.experience.id) {
        this.availabilityChecked = true;
        this.availableSlots = [];
        return;
      }

      if (!this.availabilityForm.date || this.availabilityForm.date.trim() === '') {
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('experiences.selectDateFirst') || 'Por favor, selecione uma data primeiro'
        });
        return;
      }

      const dateParam = formatDateForApi(this.availabilityForm.date);
      const requestedPeople = parseInt(this.availabilityForm.people) || 1;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDateObj = parseDateString(this.availabilityForm.date);
      const isSelectedDatePast = selectedDateObj ? selectedDateObj < today : false;

      const query = {
        experienceId: this.experience.id,
        date: dateParam,
        isArchived: false,
        pageNumber: 1,
        pageSize: 50
      };

      try {
        const response = await experienceAvailabilitiesService.getList(query);
        const list = response?.data || [];

        const slotsWithAvailability = list.map((x) => {
          const isArchived = x.isArchived === true;
          const meetsMinPeople = x.minPeople <= requestedPeople;
          const slotDate = x.availableDate || dateParam;
          const isPastTime = isSlotTimePast(slotDate, x.startTime, selectedDateObj, isSelectedDatePast);

          const availableSpots = x.availableSpots ?? x.maxPeople;
          const isFull = availableSpots <= 0;
          const isAvailable = !isArchived && meetsMinPeople && availableSpots >= requestedPeople && !isPastTime;
          const hasCapacity = meetsMinPeople && availableSpots >= requestedPeople;
          const unavailabilityReason = getUnavailabilityReason(isArchived, isPastTime, hasCapacity, isFull);

          return {
            id: x.id,
            startTime: x.startTime,
            guide: x.guide,
            price: x.price,
            ageBracketPrices: x.ageBracketPrices,
            displayPrice: x.displayPrice,
            pricePeriodId: x.pricePeriodId,
            pricePeriodName: x.pricePeriodName,
            maxPeople: x.maxPeople,
            minPeople: x.minPeople,
            availableSpots,
            date: slotDate,
            isAvailable,
            unavailabilityReason
          };
        });

        this.availableSlots = slotsWithAvailability;

        const availableSlotsOnly = slotsWithAvailability.filter(slot => slot.isAvailable);
        if (availableSlotsOnly.length === 0) {
          await this.findNextAvailableSlots(dateParam, requestedPeople);
        } else {
          this.nextAvailableSlots = [];
        }
      } catch (e) {
        console.error('Error checking availability:', e);
        this.availableSlots = [];
        this.nextAvailableSlots = [];
      } finally {
        this.availabilityChecked = true;
      }
    },
    async findNextAvailableSlots(searchDate, requestedPeople) {
      try {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const searchDateObj = searchDate ? parseDateString(searchDate) : today;

        const query = {
          experienceId: this.experience.id,
          isArchived: false,
          pageNumber: 1,
          pageSize: 100
        };

        const response = await experienceAvailabilitiesService.getList(query);
        const allSlots = response?.data || [];

        const slotsWithAvailability = allSlots.map((x) => {
          const isArchived = x.isArchived === true;
          const slotDate = x.availableDate;
          const isPastTime = isSlotTimePast(slotDate, x.startTime);

          const availableSpots = x.availableSpots ?? x.maxPeople;
          const meetsMinPeople = x.minPeople <= requestedPeople;
          const hasCapacity = meetsMinPeople && availableSpots >= requestedPeople;
          const isAvailable = !isArchived && hasCapacity && !isPastTime;

          let dateDistance = Infinity;
          const slotDateObj = parseDateString(slotDate);
          if (slotDateObj && searchDateObj) {
            const normalizedSlotDate = new Date(slotDateObj.getFullYear(), slotDateObj.getMonth(), slotDateObj.getDate());
            const normalizedSearchDate = new Date(searchDateObj.getFullYear(), searchDateObj.getMonth(), searchDateObj.getDate());
            dateDistance = Math.abs(normalizedSlotDate.getTime() - normalizedSearchDate.getTime());
          }

          return {
            id: x.id,
            startTime: x.startTime,
            guide: x.guide,
            price: x.price,
            ageBracketPrices: x.ageBracketPrices,
            displayPrice: x.displayPrice,
            pricePeriodId: x.pricePeriodId,
            pricePeriodName: x.pricePeriodName,
            maxPeople: x.maxPeople,
            minPeople: x.minPeople,
            date: slotDate,
            isAvailable,
            dateDistance
          };
        });

        const availableSlots = slotsWithAvailability
          .filter(slot => {
            if (!slot || !slot.isAvailable) return false;
            if (isSlotTimePast(slot.date, slot.startTime)) return false;
            if (slot.dateDistance === Infinity) return false;

            const slotDateObj = parseDateString(slot.date);
            if (slotDateObj && searchDateObj) {
              const normalizedSearchDate = new Date(searchDateObj.getFullYear(), searchDateObj.getMonth(), searchDateObj.getDate());
              const normalizedSlotDate = new Date(slotDateObj.getFullYear(), slotDateObj.getMonth(), slotDateObj.getDate());
              if (normalizedSlotDate.getTime() === normalizedSearchDate.getTime()) return false;
            }
            return true;
          })
          .sort((a, b) => {
            if (a.dateDistance !== b.dateDistance) {
              return a.dateDistance - b.dateDistance;
            }
            const dateA = parseDateString(a.date);
            const dateB = parseDateString(b.date);
            if (dateA && dateB && searchDateObj) {
              const normalizedSearchDate = new Date(searchDateObj.getFullYear(), searchDateObj.getMonth(), searchDateObj.getDate());
              const isAFuture = dateA >= normalizedSearchDate;
              const isBFuture = dateB >= normalizedSearchDate;
              if (isAFuture && !isBFuture) return -1;
              if (!isAFuture && isBFuture) return 1;
            }
            return (a.startTime || '').localeCompare(b.startTime || '');
          })
          .slice(0, 5);

        this.nextAvailableSlots = availableSlots;
      } catch (error) {
        console.error('Error finding next available slots:', error);
        this.nextAvailableSlots = [];
      }
    },
    // Per-person price box: when prices vary by date but every bookable date in the next year has the
    // same age-bracket prices, show those rather than the base prices (which no open date may use)
    async loadOpenDatesPrices() {
      this.openDatesAgeBracketPrices = null;
      const experience = this.experience;
      if (!experience || experience.priceType !== 'PerPerson' || !experience.hasVariablePrices) return;
      const today = new Date();
      const inAYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      try {
        const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const schedule = await experiencesService.getPriceSchedule(experience.id, iso(today), iso(inAYear));
        const sets = new Map();
        (schedule?.days || [])
          .filter(day => day.availableSlotCount > 0 && day.ageBracketPrices)
          .forEach(day => {
            const key = JSON.stringify(Object.keys(day.ageBracketPrices).sort().map(k => [k, day.ageBracketPrices[k]]));
            sets.set(key, day.ageBracketPrices);
          });
        if (sets.size === 1 && this.experience && this.experience.id === experience.id) {
          this.openDatesAgeBracketPrices = [...sets.values()][0];
        }
      } catch (e) {
        // Optional: the price box falls back to the base prices
      }
    },
    selectSlot(slot) {
      if (!slot.isAvailable) {
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: 'Este horario nao esta disponivel para reserva'
        });
        return;
      }

      this.selectedSlot = slot;
      this.showReservationForm = true;
      this.$nextTick(() => {
        const element = document.querySelector('.reservation-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    handleReservationSubmitted(reservationData) {
      this.$store.dispatch('alert/setAlert', {
        type: 'success',
        message: this.$t('reservation.successMessage')
      });
    },
    openGallery(index) {
      this.lightboxStartIndex = index ?? 0;
      this.isGalleryOpen = true;
      if (typeof document !== 'undefined') {
        document.body.classList.add('no-scroll');
      }
    },
    closeGallery() {
      this.isGalleryOpen = false;
      if (typeof document !== 'undefined') {
        document.body.classList.remove('no-scroll');
      }
    },
    nextImage() {
      if (this.currentImageIndex < this.experience.images.length - 1) {
        this.currentImageIndex++;
      }
    },
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },
    async submitLeadCapture() {
      this.showLeadCapture = false;
      this.leadForm = { name: '', email: '', phone: '', message: '' };
    },
    updateHtmlLang() {
      if (document.documentElement) {
        document.documentElement.lang = this.$i18n.locale;
      }
    }
  }
};
</script>

<style scoped>
.top-img {
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  max-width: none;
  height: clamp(240px, 45vh, 500px);
  overflow: hidden;
}

.top-img .top-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  object-fit: cover;
  width: 100vw;
  height: 100%;
  max-width: none;
}

.overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  padding: 2rem 0;
  background: linear-gradient(transparent 60%, rgba(0,0,0,0.65));
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.experience-title {
  font-size: 3rem;
  font-weight: bold;
  margin: 0;
  color: #fff;
  text-shadow: 0px 2px 16px rgba(0,0,0,0.65);
}

.experience-content {
  padding: 4rem 0;
}

.short-description {
  font-size: 1.2rem;
  margin-bottom: 3rem;
  color: #666;
}

/* Availability Section */
.availability-section {
  margin: 4rem 0;
}

.availability-section .row {
  align-items: flex-start;
}

.price-box-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  height: 100%;
}

.availability-card {
  position: relative;
  background: #ffffff;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.availability-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.availability-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.availability-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.5px;
}

/* Image Gallery */
.image-gallery-section {
  margin: 4rem 0;
}

.image-gallery-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.image-gallery-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.image-carousel {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
}

.gallery-main-image {
  width: 100%;
  height: auto;
  border-radius: 24px;
  display: block;
  object-fit: cover;
  background: #fff;
  cursor: pointer;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  font-size: 2rem;
  cursor: pointer;
  z-index: 10;
  border-radius: 50%;
  transition: all 0.3s ease;
  user-select: none;
  height: 48px;
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: var(--secondary-color);
}

.carousel-arrow:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-50%) scale(1.1);
}

.carousel-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel-arrow.left {
  left: 1rem;
}

.carousel-arrow.right {
  right: 1rem;
}

/* Description */
.description-section {
  margin: 3rem 0;
}

.no-description {
  color: #666;
  font-style: italic;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px dashed #ddd;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

:global(body.no-scroll) {
  overflow: hidden;
}

/* Responsive */
@media (max-width: 991px) {
  .experience-title {
    font-size: 2rem;
  }

  .availability-section {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }

  .availability-section .row {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }

  .availability-section .col-12.col-lg-8 {
    order: 2;
    margin-left: 0;
  }

  .availability-section .col-12.col-lg-2.price-box-wrapper {
    order: 1;
    display: block;
  }

  .availability-card {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }

  .image-gallery-card {
    padding: 0;
    border-radius: 20px;
  }

  .gallery-main-image {
    border-radius: 20px;
  }

  .carousel-arrow {
    height: 40px;
    width: 40px;
    font-size: 1.5rem;
  }

  .carousel-arrow.left {
    left: 0.5rem;
  }

  .carousel-arrow.right {
    right: 0.5rem;
  }

  .availability-title {
    font-size: 1.75rem;
  }
}

@media (max-width: 480px) {
  .availability-card {
    padding: 1.5rem 1rem;
  }

  .gallery-main-image {
    border-radius: 16px;
  }

  .carousel-arrow {
    height: 36px;
    width: 36px;
    font-size: 1.25rem;
  }

  .carousel-arrow.left {
    left: 0.25rem;
  }

  .carousel-arrow.right {
    right: 0.25rem;
  }

  .availability-title {
    font-size: 1.5rem;
  }
}
</style>
